import type {
  SlaDashboardSource,
  SlaFilterChange,
  SlaFilters,
  SlaQuestion,
  SlaQuestionFilterValue,
  SlaTag,
} from "../components/sla/slaTypes";
import {
  getSlaQuestionnaireQuestions,
  hasSlaFilterValue,
} from "../components/sla/slaTypes";
import type { RouteMetadata } from "./metadata";

export function parseProjectSearch(input: Record<string, unknown>): {
  tab?: "implementation";
} {
  return input.tab === "implementation" ? { tab: "implementation" } : {};
}

export type SlaSearch = {
  company?: string;
  startDate?: string;
  endDate?: string;
  filters?: string | Record<string, unknown>;
  page: number;
  order?: number;
};

const nonEmpty = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

export function parseNonEmptyParams<T extends Record<string, string>>(
  params: T,
): T | false {
  return Object.values(params).every(nonEmpty) ? params : false;
}

const optionalString = (value: unknown): string | undefined =>
  nonEmpty(value) ? value.trim() : undefined;

const positiveInteger = (value: unknown, fallback: number): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toCalendarDate = (value: unknown): string | undefined => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined;
    return [
      String(value.getFullYear()).padStart(4, "0"),
      String(value.getMonth() + 1).padStart(2, "0"),
      String(value.getDate()).padStart(2, "0"),
    ].join("-");
  }

  if (typeof value !== "string") return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return undefined;

  const [, year, month, day] = match;
  const parsed = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day)),
  );
  if (
    parsed.getUTCFullYear() !== Number(year) ||
    parsed.getUTCMonth() !== Number(month) - 1 ||
    parsed.getUTCDate() !== Number(day)
  ) {
    return undefined;
  }

  return `${year}-${month}-${day}`;
};

const fromCalendarDate = (value: unknown): Date | undefined => {
  const calendarDate = toCalendarDate(value);
  if (!calendarDate) return undefined;
  const [year, month, day] = calendarDate.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const toEndOfCalendarDate = (value: unknown): Date | undefined => {
  const date = fromCalendarDate(value);
  if (!date) return undefined;
  date.setHours(23, 59, 59, 999);
  return date;
};

const isValidDateRange = (value: Date[] | null): value is [Date, Date] =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every(
    (date) => date instanceof Date && !Number.isNaN(date.getTime()),
  ) &&
  value[0].getTime() <= value[1].getTime();

const encodeDateRange = (value: Date[] | null) => {
  if (!isValidDateRange(value)) return undefined;

  const startDate = toCalendarDate(value[0]);
  const endDate = toCalendarDate(value[1]);
  return startDate && endDate ? { startDate, endDate } : undefined;
};

const encodeFilterValue = (value: SlaQuestionFilterValue): unknown => {
  if (value instanceof Date) return toCalendarDate(value);
  if (Array.isArray(value)) return value.map(encodeFilterValue);
  return value;
};

const unwrapFilterValue = (value: unknown): unknown =>
  isRecord(value) && "value" in value ? value.value : value;

const getOptionValues = (field: SlaQuestion | SlaTag): Set<string> => {
  if ("_id" in field) {
    return new Set(
      (field.config?.options || []).flatMap((option) => [
        option.value,
        ...(option.previousValues || []),
      ]),
    );
  }

  return new Set(
    (field.options || [])
      .map((option) => option.id)
      .filter((id): id is string => typeof id === "string"),
  );
};

const normalizeFilterValue = (
  field: SlaQuestion | SlaTag,
  rawValue: unknown,
): SlaQuestionFilterValue => {
  const value = unwrapFilterValue(rawValue);

  if ("_id" in field && field.type === "date") {
    if (Array.isArray(value)) {
      const dates = value
        .map(fromCalendarDate)
        .filter((date): date is Date => Boolean(date));
      return dates.length === value.length ? dates : undefined;
    }
    return fromCalendarDate(value);
  }

  if ("_id" in field && field.type === "range") {
    const isNumericValue =
      (typeof value === "number" && Number.isFinite(value)) ||
      (typeof value === "string" && value.trim() !== "");
    const numberValue = Number(value);
    return isNumericValue && Number.isFinite(numberValue)
      ? numberValue
      : undefined;
  }

  if (
    ("_id" in field && field.type === "text") ||
    ("id" in field && field.type === "text")
  ) {
    return typeof value === "string" ? value : undefined;
  }

  if ("id" in field && field.type !== "single" && field.type !== "multiple") {
    return undefined;
  }

  const values = Array.isArray(value) ? value : [value];
  const validValues = values.filter(
    (item): item is string =>
      typeof item === "string" && getOptionValues(field).has(item),
  );
  return validValues.length > 0 ? validValues : undefined;
};

const parseFilterMap = (
  rawMap: unknown,
  fields: Map<string, SlaQuestion | SlaTag>,
): Record<string, SlaQuestionFilterValue> => {
  if (!isRecord(rawMap)) return {};

  return Object.fromEntries(
    Object.entries(rawMap).flatMap(([id, rawValue]) => {
      const field = fields.get(id);
      if (!field) return [];
      const value = normalizeFilterValue(field, rawValue);
      return hasSlaFilterValue(value) ? [[id, value]] : [];
    }),
  );
};

const parseFilterPayload = (value: unknown): UnknownRecord | null => {
  if (isRecord(value)) return value;
  if (typeof value !== "string") return null;

  try {
    const parsed: unknown = JSON.parse(value);
    if (isRecord(parsed)) return parsed;
    console.error("[SLA filters] Ignoring malformed URL filter payload", {
      reason: "invalid-shape",
    });
    return null;
  } catch {
    let decoded: string;
    try {
      decoded = decodeURIComponent(value);
    } catch {
      console.error("[SLA filters] Ignoring malformed URL filter payload", {
        reason: "invalid-encoding",
      });
      return null;
    }

    try {
      const parsed: unknown = JSON.parse(decoded);
      if (isRecord(parsed)) return parsed;
      console.error("[SLA filters] Ignoring malformed URL filter payload", {
        reason: "invalid-shape",
      });
      return null;
    } catch {
      console.error("[SLA filters] Ignoring malformed URL filter payload", {
        reason: "invalid-json",
      });
      return null;
    }
  }
};

const parseDateRange = (value: unknown): Date[] | null => {
  let start: unknown;
  let end: unknown;

  if (Array.isArray(value)) {
    [start, end] = value;
  } else if (isRecord(value)) {
    start = value.startDate;
    end = value.endDate;
  }

  const parsedStart = fromCalendarDate(start);
  const parsedEnd = toEndOfCalendarDate(end);
  return parsedStart &&
    parsedEnd &&
    parsedStart.getTime() <= parsedEnd.getTime()
    ? [parsedStart, parsedEnd]
    : null;
};

export const serializeSlaFilters = (
  filters: SlaFilters,
): string | undefined => {
  const questionFilters = Object.fromEntries(
    Object.entries(filters.questionFilters).flatMap(([id, value]) =>
      hasSlaFilterValue(value) ? [[id, encodeFilterValue(value)]] : [],
    ),
  );
  const tagFilters = Object.fromEntries(
    Object.entries(filters.tagFilters).flatMap(([id, value]) =>
      hasSlaFilterValue(value) ? [[id, encodeFilterValue(value)]] : [],
    ),
  );
  const dateRange = encodeDateRange(filters.dateRange);

  const payload = {
    ...(Object.keys(questionFilters).length > 0 ? { questionFilters } : {}),
    ...(Object.keys(tagFilters).length > 0 ? { tagFilters } : {}),
    ...(dateRange?.startDate && dateRange.endDate ? { dateRange } : {}),
  };

  return Object.keys(payload).length > 0 ? JSON.stringify(payload) : undefined;
};

const valuesMatch = (
  first: SlaQuestionFilterValue,
  second: SlaQuestionFilterValue,
) =>
  JSON.stringify(encodeFilterValue(first)) ===
  JSON.stringify(encodeFilterValue(second));

const patchFilterMap = (
  payload: UnknownRecord,
  key: "questionFilters" | "tagFilters",
  previous: Record<string, SlaQuestionFilterValue>,
  next: Record<string, SlaQuestionFilterValue>,
) => {
  const rawValue = payload[key];
  const rawMap = isRecord(rawValue) ? { ...rawValue } : {};
  let changed = false;

  for (const id of new Set([...Object.keys(previous), ...Object.keys(next)])) {
    const previousValue = previous[id];
    const nextValue = next[id];
    const hadPreviousValue = hasSlaFilterValue(previousValue);
    const hasNextValue = hasSlaFilterValue(nextValue);

    if (!hadPreviousValue && !hasNextValue) continue;
    if (
      hadPreviousValue &&
      hasNextValue &&
      valuesMatch(previousValue, nextValue)
    ) {
      continue;
    }

    changed = true;
    if (hasNextValue) rawMap[id] = encodeFilterValue(nextValue);
    else delete rawMap[id];
  }

  if (changed || isRecord(rawValue)) {
    if (Object.keys(rawMap).length > 0) payload[key] = rawMap;
    else delete payload[key];
  }
};

const patchDateRange = (
  payload: UnknownRecord,
  previous: Date[] | null,
  next: Date[] | null,
) => {
  const previousRange = encodeDateRange(previous);
  const nextRange = encodeDateRange(next);
  if (JSON.stringify(previousRange) === JSON.stringify(nextRange)) return;

  if (nextRange) payload.dateRange = nextRange;
  else delete payload.dateRange;
};

const removeFilterFromPayload = (
  payload: UnknownRecord,
  filter: "questionFilters" | "tagFilters",
  id: string,
) => {
  if (!isRecord(payload[filter])) return;
  const values = { ...payload[filter] };
  delete values[id];
  if (Object.keys(values).length > 0) payload[filter] = values;
  else delete payload[filter];
};

export const serializeSlaFilterChange = (
  search: Pick<SlaSearch, "filters">,
  previous: SlaFilters,
  next: SlaFilters,
  change: SlaFilterChange = { type: "apply" },
): string | undefined => {
  if (change.type === "clear") return undefined;

  const payload = parseFilterPayload(search.filters) ?? {};
  if (change.type === "remove") {
    if (change.filter === "date") delete payload.dateRange;
    else {
      removeFilterFromPayload(
        payload,
        change.filter === "question" ? "questionFilters" : "tagFilters",
        change.id,
      );
    }
  } else {
    patchFilterMap(
      payload,
      "questionFilters",
      previous.questionFilters,
      next.questionFilters,
    );
    patchFilterMap(payload, "tagFilters", previous.tagFilters, next.tagFilters);
    patchDateRange(payload, previous.dateRange, next.dateRange);
  }

  return Object.keys(payload).length > 0 ? JSON.stringify(payload) : undefined;
};

export function parseSlaFilters(
  search: Pick<SlaSearch, "filters" | "startDate" | "endDate">,
  sla: SlaDashboardSource | null | undefined,
): SlaFilters {
  const payload = parseFilterPayload(search.filters);
  const questions = new Map(
    getSlaQuestionnaireQuestions(sla?.questionnaire).map((question) => [
      question._id,
      question,
    ]),
  );
  const tags = new Map((sla?.tags || []).map((tag) => [tag.id, tag]));

  const dateRange = parseDateRange(
    payload?.dateRange ??
      (search.startDate && search.endDate
        ? { startDate: search.startDate, endDate: search.endDate }
        : undefined),
  );

  return {
    questionFilters: parseFilterMap(payload?.questionFilters, questions),
    tagFilters: parseFilterMap(payload?.tagFilters, tags),
    dateRange,
  };
}

export function parseSlaSearch(input: Record<string, unknown>): SlaSearch {
  const company = optionalString(input.company);
  const startDate = optionalString(input.startDate);
  const endDate = optionalString(input.endDate);
  const filters =
    typeof input.filters === "string"
      ? optionalString(input.filters)
      : isRecord(input.filters)
        ? input.filters
        : undefined;
  const order = positiveInteger(input.order, 0);

  return {
    ...(company ? { company } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...(filters ? { filters } : {}),
    page: positiveInteger(input.page, 1),
    ...(order ? { order } : {}),
  };
}

export function serializeSlaSearch(search: SlaSearch): string {
  const params = new URLSearchParams();
  for (const key of ["company", "startDate", "endDate", "filters"] as const) {
    const value = search[key];
    if (value) {
      params.set(
        key,
        typeof value === "string" ? value : (JSON.stringify(value) ?? ""),
      );
    }
  }
  params.set("page", String(search.page));
  if (search.order) params.set("order", String(search.order));
  return params.toString();
}

export const protectedLeaf = (metadata: RouteMetadata): RouteMetadata =>
  metadata;
